import merge from 'lodash.merge';

import { ChainName, RawTokenInfo, TokenInfo } from 'types';
import { Address, isAddress } from 'viem';
import {
  arbitrum,
  base,
  blast,
  bsc,
  Chain,
  cronos,
  hardhat,
  linea,
  mainnet,
  manta,
  metis,
  polygon,
  scroll,
  soneium,
  xLayer,
  zetachain,
} from 'wagmi/chains';

import wowmaxAbi from './abi/wowmaxAbi.json';
import arbitrumTokensList from './tokensLists/arbitrumTokensList.json';
import baseTokensList from './tokensLists/baseTokensList.json';
import blastTokensList from './tokensLists/blastTokensList.json';
import bscTokensList from './tokensLists/bscTokensList.json';
import cronosTokensList from './tokensLists/cronosTokensList.json';
import devnetTokensList from './tokensLists/devnetTokensList';
import ethereumTokensList from './tokensLists/ethereumTokensList.json';
import hyperTokensList from './tokensLists/hyperTokensList.json';
import lineaTokensList from './tokensLists/lineaTokensList.json';
import mantaPacificTokensList from './tokensLists/mantaPacificTokensList.json';
import metisTokensList from './tokensLists/metisTokensList.json';
import polygonTokensList from './tokensLists/polygonTokensList.json';
import scrollTokensList from './tokensLists/scrollTokensList.json';
import soneiumTokensList from './tokensLists/soneiumTokensList.json';
import unit0TokensList from './tokensLists/unit0TokensList.json';
import xlayerTokensList from './tokensLists/xlayerTokensList.json';
import zeroGTokensList from './tokensLists/zeroGTokensList.json';
import zetachainTokensList from './tokensLists/zetachainTokensList.json';
import { hyper, unit0, zeroGMainnet } from './customChains';

export const IS_DEV = typeof window !== 'undefined' && window.location.hostname === 'localhost';

export const CHAINS: Record<ChainName, Chain> = {
  ethereum: mainnet,
  bsc,
  cronos,
  base,
  linea,
  scroll,
  manta,
  blast,
  metis,
  xLayer,
  arbitrum,
  zetachain,
  polygon,
  devnet: hardhat,
  soneium,
  unit0,
  hyper,
  zeroG: zeroGMainnet,
};

export const CHAIN_BY_ID: Record<number, Chain> = {};
Object.values(CHAINS).forEach((c) => {
  CHAIN_BY_ID[c.id] = c;
});

const handleToken = (token: RawTokenInfo) =>
  ({
    ...token,
    address: token.address.toLowerCase(),
  }) as TokenInfo;

export const ALL_TOKENS: Record<number, TokenInfo[]> = {
  [CHAINS.ethereum.id]: ethereumTokensList.tokens.map(handleToken),
  [CHAINS.bsc.id]: bscTokensList.tokens.map(handleToken),
  [CHAINS.cronos.id]: cronosTokensList.tokens.map(handleToken),
  [CHAINS.base.id]: baseTokensList.tokens.map(handleToken),
  [CHAINS.linea.id]: lineaTokensList.tokens.map(handleToken),
  [CHAINS.scroll.id]: scrollTokensList.tokens.map(handleToken),
  [CHAINS.manta.id]: mantaPacificTokensList.tokens.map(handleToken),
  [CHAINS.blast.id]: blastTokensList.tokens.map(handleToken),
  [CHAINS.metis.id]: metisTokensList.tokens.map(handleToken),
  [CHAINS.xLayer.id]: xlayerTokensList.tokens.map(handleToken),
  [CHAINS.arbitrum.id]: arbitrumTokensList.tokens.map(handleToken),
  [CHAINS.zetachain.id]: zetachainTokensList.tokens.map(handleToken),
  [CHAINS.polygon.id]: polygonTokensList.tokens.map(handleToken),
  [CHAINS.soneium.id]: soneiumTokensList.tokens.map(handleToken),
  [CHAINS.unit0.id]: unit0TokensList.tokens.map(handleToken),
  [CHAINS.hyper.id]: hyperTokensList.tokens.map(handleToken),
  [CHAINS.zeroG.id]: zeroGTokensList.tokens.map(handleToken),
  [CHAINS.devnet.id]: devnetTokensList.tokens.map(handleToken),
};

export const TOKEN_BY_ADDRESS: Record<number, Record<Address, TokenInfo | undefined>> = {};
export const NATIVE_TOKENS: Record<number, TokenInfo> = {};
export const WRAPPED_NATIVE_TOKENS: Record<number, TokenInfo> = {};

export const addTokenToConsts = (t: TokenInfo, isCustomToken: boolean) => {
  const chainId = +t.chainId;
  const token = isCustomToken ? merge({}, t, TOKEN_BY_ADDRESS[chainId][t.address] || {}) : t;

  TOKEN_BY_ADDRESS[chainId][token.address] = token;

  if (isCustomToken) {
    const existingToken = ALL_TOKENS[chainId].find((t) => t.address === token.address);
    if (existingToken) {
      existingToken.tags?.push('custom');
    } else {
      ALL_TOKENS[chainId].push(token);
    }
    return;
  }
  if (token.tags?.includes('native')) {
    NATIVE_TOKENS[chainId] = token;
    TOKEN_BY_ADDRESS[chainId]['0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'] = token;
  }
  if (token.tags?.includes('wrappedNative')) {
    WRAPPED_NATIVE_TOKENS[chainId] = token;
  }
};

export const removeCustomTokenFromConsts = (token: TokenInfo) => {
  const chainId = +token.chainId;
  const existingToken = ALL_TOKENS[chainId].find((t) => t.address === token.address);
  if (!existingToken) return;
  if (existingToken.tags?.includes('hidden')) {
    existingToken.tags = [...existingToken.tags.filter((tag) => tag !== 'custom')];
  } else {
    ALL_TOKENS[chainId] = [...ALL_TOKENS[chainId].filter((t) => t.address !== token.address)];
  }
};

Object.entries(ALL_TOKENS).forEach(([chainId, tokens]) => {
  TOKEN_BY_ADDRESS[+chainId] = {};

  tokens.forEach((token) => {
    addTokenToConsts(token, false);
  });
});

(() => {
  if (typeof window === 'undefined') return;
  try {
    const customTokensStr = localStorage.getItem('custom-tokens');
    if (!customTokensStr) return;
    const customTokens = JSON.parse(customTokensStr);
    Object.values(customTokens).forEach((tokens) => {
      (tokens as TokenInfo[]).forEach((token) => {
        if (!isAddress(token.address)) throw new Error('Incorrect custom token');
        addTokenToConsts(token, true);
      });
    });
  } catch (err) {
    console.log('err', err);
    localStorage.removeItem('custom-tokens');
  }
})();

// INFO: added in commonStore.checkSwapRouterContracts
export const ROUTER_ADDRESSES: Record<number, Address> = {};

export const routerAbi = wowmaxAbi;

const DISABLED_CHAINS: number[] = [CHAINS.xLayer.id];

export const SUPPORTED_CHAINS = Object.values(CHAINS)
  .map((c) => c.id)
  .filter((c) => c !== CHAINS.devnet.id)
  .filter((c) => !DISABLED_CHAINS.includes(c));

export const DEFAULT_CHAIN_ID = CHAINS.ethereum.id;

export const WETH_SUPPORTED_CHAINS = [
  CHAINS.linea.id,
  CHAINS.arbitrum.id,
  CHAINS.scroll.id,
  CHAINS.base.id,
  CHAINS.bsc.id,
  CHAINS.ethereum.id,
  CHAINS.soneium.id,
  CHAINS.unit0.id,
  CHAINS.hyper.id,
  CHAINS.cronos.id,
  CHAINS.zeroG.id,
];

export const RPC_MAP: Record<number, string> = {
  [CHAINS.ethereum.id]: 'https://nodes-ethereum.wowmax.exchange/',
  [CHAINS.bsc.id]: 'https://nodes-binance.wowmax.exchange/',
  [CHAINS.cronos.id]: 'https://nodes-cronos.wowmax.exchange/',
  [CHAINS.base.id]: 'https://nodes-base.wowmax.exchange/',
  [CHAINS.linea.id]: 'https://nodes-linea.wowmax.exchange/',
  [CHAINS.scroll.id]: 'https://nodes-scroll.wowmax.exchange/',
  [CHAINS.manta.id]: 'https://nodes-manta.wowmax.exchange/',
  [CHAINS.blast.id]: 'https://nodes-blast.wowmax.exchange/',
  [CHAINS.metis.id]: 'https://nodes-metis.wowmax.exchange/',
  [CHAINS.xLayer.id]: 'https://nodes-x1.wowmax.exchange/',
  [CHAINS.arbitrum.id]: 'https://nodes-arbitrum.wowmax.exchange/',
  [CHAINS.zetachain.id]: 'https://nodes-zeta.wowmax.exchange/',
  [CHAINS.polygon.id]: 'https://polygon.drpc.org',
  [CHAINS.soneium.id]: 'https://nodes-soneium.wowmax.exchange/',
  [CHAINS.unit0.id]: 'https://nodes-unit-zero.wowmax.exchange/',
  [CHAINS.hyper.id]: 'https://hyperliquid-node.invisium.com/',
  [CHAINS.zeroG.id]: 'https://nodes-0g.wowmax.exchange',
  [CHAINS.devnet.id]: 'http://localhost:8545/',
};

export const SCAN_NAMES: Record<number, string> = {};
Object.values(CHAINS).forEach((c) => {
  SCAN_NAMES[c.id] = c.blockExplorers?.default.name || '';
  if (c.id === CHAINS.linea.id) {
    SCAN_NAMES[c.id] = 'Lineascan';
  }
});

export const WOWMAX_API_URL = 'https://api-gateway.wowmax.exchange';

export const initialRequestState = {
  data: null,
  isLoading: false,
  isError: false,
};

export const PRICE_IMPACT_GRADES = {
  warning: 1,
  danger: 5,
};

export const SLIPPAGE_GRADES = {
  tooSmall: 0.5,
  tooHight: 5,
  max: 20,
};

export const NETWORK_SWITCH_DELAY = 2_000;

export const LOCAL_STORAGE_KEYS = {
  FIXED_TOKENS: 'fixed-tokens',
  SWAP_SETTINGS: 'swap-settings',
  CUSTOM_TOKENS: 'custom-tokens',
  LANG: 'lang',
};

export const META = {
  title: 'WOWMAX',
  description:
    'Swap at best prices, trade cross-chain on 20+ networks, and automatically copy-trade top DEX traders: all-in-one non-custodial platform.',
};
